import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showNotification, updateNotification } from '@mantine/notifications';
import { QUERY_KEYS } from '@/constants/queryKeys';
import { messageTemplatesService } from '@/services/messageTemplates.service';
import type { CreateTagPayload, UpdateTemplatePayload } from '@/types/messageTemplates';

const TAG_CREATE_NOTIFICATION_ID = 'tag-create';
const TAG_DELETE_NOTIFICATION_ID = 'tag-delete';
const TEMPLATE_DELETE_NOTIFICATION_ID = 'template-delete';
const TEMPLATE_STATUS_NOTIFICATION_ID = 'template-status';

export const useCreateTagMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTagPayload) => messageTemplatesService.createTag(data),
    onMutate: () => {
      showNotification({
        id: TAG_CREATE_NOTIFICATION_ID,
        title: 'Creating tag',
        message: 'Please wait...',
        loading: true,
        autoClose: false,
        withCloseButton: false,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MESSAGE_TEMPLATE_TAGS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MESSAGE_TEMPLATES] });
      updateNotification({
        id: TAG_CREATE_NOTIFICATION_ID,
        title: 'Success',
        message: 'Tag created successfully',
        color: 'green',
        loading: false,
        autoClose: 3000,
        withCloseButton: true,
      });
    },
    onError: (error) => {
      updateNotification({
        id: TAG_CREATE_NOTIFICATION_ID,
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create tag',
        color: 'red',
        loading: false,
        autoClose: 4000,
        withCloseButton: true,
      });
    },
  });
};

export const useDeleteTagMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => messageTemplatesService.deleteTag(id),
    onMutate: () => {
      showNotification({
        id: TAG_DELETE_NOTIFICATION_ID,
        title: 'Deleting tag',
        message: 'Please wait...',
        loading: true,
        autoClose: false,
        withCloseButton: false,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MESSAGE_TEMPLATE_TAGS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MESSAGE_TEMPLATES] });
      updateNotification({
        id: TAG_DELETE_NOTIFICATION_ID,
        title: 'Success',
        message: 'Tag deleted successfully',
        color: 'green',
        loading: false,
        autoClose: 3000,
        withCloseButton: true,
      });
    },
    onError: (error) => {
      updateNotification({
        id: TAG_DELETE_NOTIFICATION_ID,
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete tag',
        color: 'red',
        loading: false,
        autoClose: 4000,
        withCloseButton: true,
      });
    },
  });
};

export const useDeleteTemplateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => messageTemplatesService.deleteTemplate(id),
    onMutate: () => {
      showNotification({
        id: TEMPLATE_DELETE_NOTIFICATION_ID,
        title: 'Deleting template',
        message: 'Please wait...',
        loading: true,
        autoClose: false,
        withCloseButton: false,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MESSAGE_TEMPLATES] });
      updateNotification({
        id: TEMPLATE_DELETE_NOTIFICATION_ID,
        title: 'Success',
        message: 'Template deleted successfully',
        color: 'green',
        loading: false,
        autoClose: 3000,
        withCloseButton: true,
      });
    },
    onError: (error) => {
      updateNotification({
        id: TEMPLATE_DELETE_NOTIFICATION_ID,
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete template',
        color: 'red',
        loading: false,
        autoClose: 4000,
        withCloseButton: true,
      });
    },
  });
};

export const useUpdateTemplateStatusMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTemplatePayload }) =>
      messageTemplatesService.updateTemplate(id, payload),
    onMutate: () => {
      showNotification({
        id: TEMPLATE_STATUS_NOTIFICATION_ID,
        title: 'Updating template status',
        message: 'Please wait...',
        loading: true,
        autoClose: false,
        withCloseButton: false,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MESSAGE_TEMPLATES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MESSAGE_TEMPLATE_BY_ID] });
      updateNotification({
        id: TEMPLATE_STATUS_NOTIFICATION_ID,
        title: 'Success',
        message: 'Template status updated successfully',
        color: 'green',
        loading: false,
        autoClose: 3000,
        withCloseButton: true,
      });
    },
    onError: (error) => {
      updateNotification({
        id: TEMPLATE_STATUS_NOTIFICATION_ID,
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update template status',
        color: 'red',
        loading: false,
        autoClose: 4000,
        withCloseButton: true,
      });
    },
  });
};
